import { useTranslation } from 'next-i18next'
import { ForwardedRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { useIsExpressPayEnabled } from '@src/modules/express-pay/express-pay'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import Product from '@src/quince-site-common/src/domains/Product'
import { BaseProductVariant } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper, { THIRD_PARTY } from '@src/quince-site-common/src/helpers/productHelper'
import * as svdCascadeHelper from '@src/quince-site-common/src/helpers/svd-cascade-helper'
import { useShopInAppHandler } from '@src/quince-site-common/src/hooks/appQrModal'
import { useIsSvdEnabled } from '@src/quince-site-common/src/hooks/use-is-svd-enabled'
import useDeviceInfo from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useHideKlarnaExp from '@src/quince-site-common/src/hooks/useHideKlarnaExp'
import useKlarnaModule from '@src/quince-site-common/src/hooks/useKlarnaModule'
import useRecurringItemSubscriptionsExperiment from '@src/quince-site-common/src/hooks/useRecurringItemSubscriptionsExperiment'
import useTrueFitWidgetVisibility from '@src/quince-site-common/src/organisms/true-fit/hooks/useTrueFitWidgetVisibility'
import productService from '@src/quince-site-common/src/services/productService'
import { GROUP_OPTION_NAMES } from '@src/quince-site-common/src/utils/constants'
import { getIsFinalSaleProduct } from '@src/quince-site-common/src/utils/festivalOffersUtility'
import { runAfterInteractionPaint } from '@src/quince-site-common/src/utils/interaction-scheduling'
import useProductPageContext from '@src/quince-site-pdp/src/hooks/useProductContext'
import { getProductDetailsIcons } from '@src/quince-site-pdp/src/organisms/productPageSections/productDetailsAndCare/utils'
import { getProductPropertiesForTracking, isMiniPDP } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import useHash from '../../hooks/useHash'
import { CTA_TYPE, LOW_INVENTORY_THRESHOLD } from '../productCTA/productCTAConstants'
import { getUniqueColorGroupings } from '../productCTA/utils'
import { PURCHASE_TYPES } from '../purchaseTypeSelector'

import { AIRLINE_COMPATIBILITY_HASH, shouldShowOnlySizeChart } from './product-form-shared'
import { ProductFormProps } from './product-form.types'

interface ProductFormExpData {
  productFormWrapperClass?: string
  moveAddToCartBtnToRight?: boolean
  showViewFullProductDetails?: boolean
  hideProductDetail?: boolean
  hideReviewCount?: boolean
  viewFullProductText?: string
  productInfoHeaderTextClassName?: string
  hideProductDescFooter?: boolean
  hideReviewLabel?: boolean
  productFormOptionsClass?: string
  productInfoReviewClassName?: string
  inStockMsgClass?: string
  showReadMoreInDesc?: boolean
  showInlineSizeChart?: boolean
  hideSizeChart?: boolean
}

/**
 * Stable empty SVD selection. Legacy callers never pass `svdSelected`, so falling back to a shared
 * frozen array keeps referential identity across renders — a fresh `[]` each render would churn the
 * `selectedVariant` memo deps on every legacy PDP render for no reason. Exported so `product-form-legacy.tsx`
 * reuses the same identity instead of its own `[]` literal.
 */
export const EMPTY_SVD_SELECTION: NonNullable<ProductFormProps['svdSelected']> = []

export function useProductForm(
  props: ProductFormProps,
  ref: ForwardedRef<HTMLDivElement | null>,
  freeSwatchClassName: string,
) {
  const { t } = useTranslation('common')
  const productFormRef = useRef<HTMLDivElement>(null)
  const { locale } = useLocaleContext()
  const { storeProgramResponse } = useStorefrontModeContext()
  // SVD flow vs legacy — branched, never mixed (see pdp-purchase-widget-solutioning.md).
  const isSvd = useIsSvdEnabled()
  const svdSelected = props.svdSelected ?? EMPTY_SVD_SELECTION

  const newColorGroupings = getUniqueColorGroupings(props.product?.variants ?? [])

  const selectedVariant = useMemo(
    () =>
      props.product
        ? ((isSvd
            ? svdCascadeHelper.getSvdVariantWithMinPrice(props.product, svdSelected)
            : productHelper.getVariantWithMinQuincePriceForOptions(props.product, props.selectedOptions)) ??
          ({} as BaseProductVariant))
        : ({} as BaseProductVariant),
    [props.product, props.selectedOptions, isSvd, svdSelected],
  )

  useImperativeHandle(ref, () => {
    if (!productFormRef.current) {
      // @i18n-ignore
      throw new Error('productFormRef is not assigned')
    }
    return productFormRef.current
  })

  const { showFullPDP, setShowFullPDP, productPageExpData } = useProductPageContext()
  // ProductPageExpValue's `[key: string]: any` index signature widens these fields to `any`; narrow them here.
  const {
    productFormWrapperClass,
    moveAddToCartBtnToRight,
    showViewFullProductDetails,
    hideProductDetail,
    hideReviewCount,
    viewFullProductText,
    productInfoHeaderTextClassName,
    hideProductDescFooter,
    hideReviewLabel,
    productFormOptionsClass,
    productInfoReviewClassName,
    inStockMsgClass,
    showReadMoreInDesc,
    showInlineSizeChart,
    hideSizeChart,
  } = productPageExpData as ProductFormExpData

  const { hideKlarna } = useHideKlarnaExp()

  const isFabricMergedProduct = props.product?.addOnProductOptionGroups?.find(
    option => option.groupOptionName == GROUP_OPTION_NAMES.FABRIC,
  )
  const initialSwatch = isFabricMergedProduct ? [] : [props.product?.swatch]

  const [showSizeChart, setShowSizeChart] = useState(false)
  const [showAirlineCompatibility, setShowAirlineCompatibility] = useState(false)
  const [swatches, setSwatches] = useState(initialSwatch)
  const { isAppQRModalOpen, handleShopInApp, handleCloseAppQRModal } = useShopInAppHandler()
  const isRecurringItemSubscriptionsExpEnabled = useRecurringItemSubscriptionsExperiment()

  const isExpressPayEnabled = useIsExpressPayEnabled()
  const showExpressPay = props.expressPayConfig && !props.expressPayConfig.disabled && isExpressPayEnabled
  const shouldKeepDefaultDesktopExpressPayRowMounted =
    showExpressPay && !props.isQuickATCForMobile && !props.isPostOrderFlow

  const optionNameSelectedValueMap = props.selectedOptions.reduce((map: Record<string, string>, option) => {
    map[option.name] = option.value
    return map
  }, {})

  const isVariantThirdParty = selectedVariant.vendorType === THIRD_PARTY

  // `isStale` lets the caller drop a response whose effect run has been superseded by a newer
  // product, so a slow earlier fetch can't overwrite the current product's swatches.
  const fetchProductWithSwatch = async (isStale?: () => boolean) => {
    try {
      const products = await productService.getProducts({
        spids: props.product?.addOnProductOptionGroups
          ?.find(option => option.groupOptionName == GROUP_OPTION_NAMES.FABRIC)
          ?.groupItems.map(item => item.spid),
      })
      if (isStale?.()) return
      let currentFabricSelectedSwatch
      const productSwatches = products
        .filter(product => {
          if (product.spid == props.product?.spid) {
            currentFabricSelectedSwatch = product.swatch
            return false
          } else {
            return true
          }
        })
        .map(product => product.swatch)
      setSwatches([currentFabricSelectedSwatch, ...productSwatches])
    } catch {
      // getProducts wraps failures in QuinceApiError; degrade gracefully to the initial swatch
      // list so a failed fetch never surfaces as an unhandled rejection or stale swatch state.
      if (isStale?.()) return
      setSwatches(initialSwatch)
    }
  }

  useEffect(() => {
    if (props.product?.addOnProductOptionGroups?.find(option => option.groupOptionName == GROUP_OPTION_NAMES.FABRIC)) {
      let stale = false
      void fetchProductWithSwatch(() => stale)
      return () => {
        stale = true
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.product])

  const [hash, _setHashValue] = useHash()

  useEffect(() => {
    if (hash === AIRLINE_COMPATIBILITY_HASH) {
      setShowAirlineCompatibility(true)
    }
  }, [hash])

  const handleSizeChart = () => {
    // Open the modal synchronously — it is the direct, visible response to the click.
    // Only the analytics side-effect is deferred off the interaction for INP (gated).
    setShowSizeChart(true)
    runAfterInteractionPaint(() => {
      const productProperties = getProductPropertiesForTracking(props.product as Product)
      trackEvent(TELEMETRY_EVENT_NAMES.SIZE_CHART_CLICKED, {
        section_type: 'product_form',
        context: {
          size_chart_name: props.product?.sizeChart?.title,
          productProperties: productProperties,
        },
      })
    })
  }

  const hideAirlineCompatbility = () => {
    setShowAirlineCompatibility(false)
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      url.hash = ''
      window.history.replaceState(null, '', url.toString())
      // eslint-disable-next-line i18next/no-literal-string
      window.dispatchEvent(new HashChangeEvent('hashchange'))
    }
  }

  const selectedVariantAvailableInventory = props.selectedVariantPurchaseInfo?.availableInventory ?? 0

  const { onSwatchLinkClick } = props
  const renderSwatchSelectionButton = useCallback(() => {
    return swatches.filter(item => item).length ? (
      <button
        className={freeSwatchClassName}
        onClick={() => {
          onSwatchLinkClick?.(swatches.filter(item => !!item))
        }}
      >
        {t('order_free_swatches')}
      </button>
    ) : null
  }, [swatches, onSwatchLinkClick, freeSwatchClassName, t])

  useKlarnaModule(props.salePrice, locale)

  const { showTruefitWidget } = useTrueFitWidgetVisibility(props.product)

  // SVD: final-sale/festival pricing is deferred (no VDA-selection equivalent yet) — treat as non-final-sale.
  const isFinalSaleProduct = isSvd ? false : getIsFinalSaleProduct(props.product, props.selectedOptions)

  const isB2BFinalSaleProduct =
    props.isB2BSelfService && (storeProgramResponse?.storeProgramConfig.isFinalSale ?? false)

  const showVariantAvailableInventory =
    ![CTA_TYPE.WAITLIST, CTA_TYPE.PREORDER].includes(props.productCTAType) &&
    selectedVariantAvailableInventory > 0 &&
    selectedVariantAvailableInventory < LOW_INVENTORY_THRESHOLD

  // SVD has no `ProductOptionName.Size` / `isHidden` concept yet, so the size-chart-only mode never applies.
  const showOnlySizeChart = isSvd
    ? false
    : shouldShowOnlySizeChart(
        props.productOptions,
        props.sizeChart,
        props.mergedSizeChart,
        props.hideSizeChart ?? hideSizeChart,
      )

  const hideSavings = props.product?.hideSavings ?? false
  const isMiniPDPPage = props?.product?.slug ? isMiniPDP(props.product.slug) : false
  const productDetailsIcons = getProductDetailsIcons(props.product as Product)

  const showProductDescription = !hideProductDetail && !props.hideProductDescription && props.description

  const deviceInfo = useDeviceInfo()
  const isPDP = !props.isFromCartPopup && !props.isBuyNow && !props.isPostOrderFlow

  const hideKlarnaFull =
    hideKlarna || (props.showPurchaseTypeSelector && props.purchaseOption === PURCHASE_TYPES.SUBSCRIBE)

  return {
    t,
    locale,
    storeProgramResponse,
    productFormRef,
    showFullPDP,
    setShowFullPDP,
    productFormWrapperClass,
    moveAddToCartBtnToRight,
    showViewFullProductDetails,
    hideProductDetail,
    hideReviewCount,
    viewFullProductText,
    productInfoHeaderTextClassName,
    hideProductDescFooter,
    hideReviewLabel,
    productFormOptionsClass,
    productInfoReviewClassName,
    inStockMsgClass,
    showReadMoreInDesc,
    showInlineSizeChart,
    hideSizeChart,
    hideKlarna,
    hideKlarnaFull,
    isRecurringItemSubscriptionsExpEnabled,
    isExpressPayEnabled,
    showExpressPay,
    shouldKeepDefaultDesktopExpressPayRowMounted,
    optionNameSelectedValueMap,
    isVariantThirdParty,
    newColorGroupings,
    selectedVariant,
    showSizeChart,
    setShowSizeChart,
    showAirlineCompatibility,
    handleShopInApp,
    isAppQRModalOpen,
    handleCloseAppQRModal,
    showTruefitWidget,
    handleSizeChart,
    hideAirlineCompatbility,
    selectedVariantAvailableInventory,
    renderSwatchSelectionButton,
    isFinalSaleProduct,
    isB2BFinalSaleProduct,
    showVariantAvailableInventory,
    showOnlySizeChart,
    hideSavings,
    isMiniPDPPage,
    productDetailsIcons,
    showProductDescription,
    deviceInfo,
    isPDP,
  }
}
